import { X } from 'lucide-react'
import { useEffect, type RefObject, useState } from 'react'
import useOnclickOutside from 'react-cool-onclickoutside'
import { type MapRef, type ViewState } from 'react-map-gl'
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete'
import { PLOT_ZOOM_THRESHOLD } from '#app/consts'
import { Button } from '../ui/button'
import { Command, CommandInput, CommandItem, CommandList } from '../ui/command'

export const PlacesSearch = ({
	mapRef,
	viewState,
}: {
	mapRef?: RefObject<MapRef>
	viewState: ViewState
}) => {
	const {
		init,
		ready,
		value,
		suggestions: { status, data },
		setValue,
		// clearSuggestions,
	} = usePlacesAutocomplete({
		initOnMount: false,
		// requestOptions: {
		//   locationBias: {
		//     lat: viewState?.latitude,
		//     lng: viewState?.longitude,
		//   },
		// },
		debounce: 300,
	})

	const [hideSuggestions, setHideSuggestions] = useState(false)

	useEffect(() => {
		if (window.google) {
			init()
		}
	}, [init])

	const ref = useOnclickOutside(() => setHideSuggestions(true))

	const handleInput = (value: string) => setValue(value)

	const handleSelect =
		({ description }: google.maps.places.AutocompletePrediction) =>
		() => {
			setValue(description, false)
			setHideSuggestions(true)
			getGeocode({ address: description }).then(results => {
				const bounds = results[0].geometry.bounds

				if (!mapRef?.current) return

				if (bounds) {
					mapRef.current.fitBounds([
						[bounds.getSouthWest().lng(), bounds.getSouthWest().lat()],
						[bounds.getNorthEast().lng(), bounds.getNorthEast().lat()],
					])
					return
				}

				const { lat, lng } = results[0].geometry.location
				mapRef.current.flyTo({
					center: [lng(), lat()],
					zoom: PLOT_ZOOM_THRESHOLD,
				})
			})
		}

	const showList = status === 'OK' && !hideSuggestions && data.length > 0

	return (
		<div
			className="fixed left-14 top-4 z-10 w-52 rounded-lg shadow md:left-20 md:w-96"
			ref={ref}
		>
			<Command shouldFilter={false}>
				<CommandInput
					value={value}
					onValueChange={handleInput}
					disabled={!ready}
					placeholder="e.g. Amsterdam"
					onFocus={() => setHideSuggestions(false)}
					className="h-10 pr-6 "
				/>
				{value && (
					<Button
						className="absolute right-3 top-3 h-4 w-4"
						variant="link"
						onClick={() => {
							setValue('')
							setHideSuggestions(true)
						}}
					>
						<X className="h-4 w-4" />
					</Button>
				)}

				{showList && (
					<CommandList>
						{data.map((suggestion, index) => {
							const {
								place_id,
								structured_formatting: { main_text, secondary_text },
							} = suggestion

							return (
								<CommandItem
									key={`${place_id}-${index}`}
									onSelect={handleSelect(suggestion)}
								>
									<div className="flex flex-col">
										<strong>{main_text}</strong>
										<small>{secondary_text}</small>
									</div>
								</CommandItem>
							)
						})}
					</CommandList>
				)}
			</Command>
		</div>
	)
}
